'use client';

import Image from 'next/image';

import { ScrollAnimation } from '@/shared/components/ui/scroll-animation';
import { cn } from '@/shared/lib/utils';

export interface FeatureShowcaseItem {
  label?: string;
  title: string;
  description: string;
  image: {
    src: string;
    alt: string;
  };
  video?: {
    src: string;
    poster?: string;
  };
  image_position?: 'left' | 'right';
}

export interface FeatureShowcaseProps {
  items: FeatureShowcaseItem[];
  className?: string;
}

export function FeatureShowcase({ items, className }: FeatureShowcaseProps) {
  return (
    <section className={cn('py-16 md:py-24', className)}>
      <div className="container">
        <div className="space-y-16 md:space-y-24">
          {items.map((item, idx) => {
            // Alternate image position if not specified
            const imagePosition =
              item.image_position || (idx % 2 === 0 ? 'right' : 'left');

            return (
              <div
                key={idx}
                className={cn(
                  'grid items-center gap-8 md:grid-cols-2 md:gap-12 lg:gap-16',
                  imagePosition === 'left' && 'md:[&>*:first-child]:order-2'
                )}
              >
                {/* Text Content */}
                <ScrollAnimation
                  direction={imagePosition === 'left' ? 'right' : 'left'}
                  delay={0.1}
                >
                  <div className="space-y-4 md:space-y-6">
                    {item.label && (
                      <span className="bg-muted text-muted-foreground inline-block rounded-full px-4 py-1.5 text-sm font-medium">
                        {item.label}
                      </span>
                    )}
                    <h2 className="text-foreground text-2xl font-bold tracking-tight md:text-3xl lg:text-4xl">
                      {item.title}
                    </h2>
                    <p className="text-muted-foreground text-base leading-relaxed md:text-lg">
                      {item.description}
                    </p>
                  </div>
                </ScrollAnimation>

                {/* Image or Video */}
                <ScrollAnimation
                  direction={imagePosition === 'left' ? 'left' : 'right'}
                  delay={0.2}
                >
                  <div className="relative overflow-hidden rounded-2xl border shadow-lg">
                    {item.video?.src ? (
                      <video
                        src={item.video.src}
                        poster={item.video.poster || item.image?.src}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="h-auto w-full object-cover"
                      />
                    ) : (
                      <Image
                        src={item.image.src}
                        alt={item.image.alt}
                        width={600}
                        height={400}
                        className="h-auto w-full object-cover"
                      />
                    )}
                  </div>
                </ScrollAnimation>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

